import { Cart, CartItem } from 'modules/cart'
import { formatDeliveryExtendedData } from 'modules/delivery'
import { formatPrice } from 'modules/products'
import { IS_TMS_DELIVERY } from 'shared/config'
import { isNotEmpty, isNullOrUndefined } from 'shared/utils/ramda'
import { LocalStorage } from 'shared/utils/storage'

import { SaleOrder } from '../../types'
import { PaymentCodes,PaymentMethod  } from '../payment/types'

import { IS_EDIT_MODE_ENABLED } from './config'
import { CartChanges, EditSaleOrderCartKeys, ProductChanges } from './types'

export const getEditDataFromLocalStorage = () => {
  const startCart = getStorageItem<Cart>(EditSaleOrderCartKeys.EDIT_MODE_START_CART)
  const startPaymentMethod = getStorageItem<PaymentMethod>(
    EditSaleOrderCartKeys.EDIT_MODE_START_PAYMENT_METHOD
  )
  const startOrder = getStorageItem<SaleOrder>(
    EditSaleOrderCartKeys.EDIT_MODE_START_ORDER
  )

  return { startCart, startPaymentMethod, startOrder }
}

export const getStartSaleOrderFromLocalStorage = () => {
  const startOrder = getStorageItem<SaleOrder>(
    EditSaleOrderCartKeys.EDIT_MODE_START_ORDER
  )

  return startOrder
}

const getStorageItem = <T>(key: string): T | null => {
  const storage = new LocalStorage(key)

  return storage.get(null)
}

const formatDeliveryTime = (slot: Cart['deliverySlot']): string | undefined =>
  slot ? `${slot.start} - ${slot.end}` : undefined

const calculateProductDifference = (
  items: CartItem[],
  compareItems: CartItem[]
): number =>
  items.filter(
    item => !compareItems.some(compareItem => compareItem.id === item.id)
  ).length

const calculateProductChanges = (
  current: CartItem[],
  start: CartItem[]
): ProductChanges => ({
  length: current.length,
  productsAdded: calculateProductDifference(current, start),
  productsRemoved: calculateProductDifference(start, current)
})

const calculatePaymentDiff = (
  currentCart: Cart,
  startOrder: SaleOrder
): number => currentCart.paymentValue.value - startOrder.paymentValue.value

const formatAddressLines = (address: Cart['deliveryAddress']) => ({
  name: address?.name ?? null,
  firstLine: address?.street
    ? `ul. ${address.street} ${address.houseNumber ?? ''} `
    : null,
  secondLine: address?.postalCode ?? null
})

const formatDiscountCode = (codes: Cart['discountCodes']) => {
  const hasDiscountCodes = isNotEmpty(codes)

  return {
    hasDiscountCodes,
    name: hasDiscountCodes ? codes[0].code : null,
    value: hasDiscountCodes ? codes[0].value.formatted : null
  }
}

const formatPaymentInfo = (
  current: { diff?: number | null; paid?: number | null },
  start: { paid?: number | null },
  paymentMethodCode?: string
) => ({
  current: {
    diff:
      typeof current.diff === 'number'
        ? paymentMethodCode === PaymentCodes.ON_DELIVERY
          ? formatPrice(current.paid ?? 0)
          : formatPrice(current.diff)
        : null,
    paid: typeof current.paid === 'number' ? formatPrice(current.paid) : null,
    diffValue: current.diff
  },
  start: {
    paid: typeof start.paid === 'number' ? formatPrice(start.paid) : null
  }
})

export const compareCartChanges = (
  currentCart: Cart,
  currentPaymentMethod: PaymentMethod | null
): CartChanges | null => {
  const { startCart, startOrder, startPaymentMethod } =
    getEditDataFromLocalStorage()

  if (!startCart || !startPaymentMethod || !startOrder) {
    return null
  }

  return {
    orderId: startOrder.id,
    orderNo: startOrder.code,
    products: {
      current: calculateProductChanges(currentCart.items, startCart.items),
      start: {
        length: startCart.items.length,
        productsAdded: 0,
        productsRemoved: 0
      }
    },
    deliveryTime: {
      current: {
        date: currentCart.deliverySlot?.date,
        time: IS_TMS_DELIVERY
          ? formatDeliveryExtendedData(
            currentCart.tmsReservationSlotData?.slotExtended?.from,
            currentCart.tmsReservationSlotData?.slotExtended?.to
          )
          : formatDeliveryTime(currentCart.deliverySlot)
      },
      start: {
        date: startCart.deliverySlot?.date,
        time: IS_TMS_DELIVERY
          ? formatDeliveryExtendedData(
            startCart.tmsReservationSlotData?.slotExtended?.from,
            startCart.tmsReservationSlotData?.slotExtended?.to
          )
          : formatDeliveryTime(startCart.deliverySlot)
      }
    },
    deliveryAddress: {
      current: currentCart.deliveryAddress,
      start: startCart.deliveryAddress
    },
    paymentMethod: {
      current: currentPaymentMethod || startPaymentMethod,
      start: startPaymentMethod
    },
    discountCodes: {
      current: currentCart.discountCodes,
      start: startCart.discountCodes
    },
    payment: {
      current: {
        diff: calculatePaymentDiff(currentCart, startOrder),
        paid: currentCart.paymentValue.value
      },
      start: {
        paid: startOrder.paymentValue.value
      }
    }
  }
}

export const getChangesInStructuredFormat = (
  currentCart: Cart,
  currentPaymentMethod: PaymentMethod | null
) => {
  const changes = compareCartChanges(currentCart, currentPaymentMethod)

  if (!changes) {
    return null
  }

  return {
    orderId: changes.orderId,
    orderNo: changes.orderNo,
    wasOnDelivery:
      changes.paymentMethod.start?.code === PaymentCodes.ON_DELIVERY,
    isBlik: checkIfBlikCodeIsNeeded(changes),
    products: changes.products && {
      current: {
        length: `${changes.products.current.length} ${
          changes.products.current.length > 1 ? 'produktów' : 'produkt'
        }`,
        productsAdded:
          changes.products.current.productsAdded > 0
            ? `Dodano: ${changes.products.current.productsAdded}`
            : null,
        productsRemoved:
          changes.products.current.productsRemoved > 0
            ? `Usunięto: ${changes.products.current.productsRemoved}`
            : null
      },
      start: {
        length: `${changes.products.start.length} ${
          changes.products.start.length > 1 ? 'produktów' : 'produkt'
        }`
      }
    },
    deliveryAddress: {
      current: formatAddressLines(changes.deliveryAddress.current),
      start: formatAddressLines(changes.deliveryAddress.start)
    },
    deliveryTime: changes.deliveryTime,
    paymentMethod: {
      current: {
        name: changes.paymentMethod.current?.name ?? null,
        id: changes.paymentMethod.current?.id
      },
      start: {
        name: changes.paymentMethod.start?.name ?? null
      }
    },
    discountCodes: {
      current: formatDiscountCode(changes.discountCodes.current),
      start: formatDiscountCode(changes.discountCodes.start)
    },
    payment: formatPaymentInfo(
      changes.payment.current,
      changes.payment.start,
      changes.paymentMethod.start?.code
    )
  }
}

export type EditSaleOrderStatus = ReturnType<typeof getChangesInStructuredFormat>

export const getProperReturnLabel = (
  price: number | null | undefined
): string => {
  if (isNullOrUndefined(price)) return '---'

  return price > 0 ? 'Do dopłaty' : 'Do zwrotu'
}

export const getProperReturnValue = (
  price: number | null | undefined
): string | null => {
  if (isNullOrUndefined(price)) return null

  return formatPrice(Math.abs(Number(price)))
}

const checkIfBlikCodeIsNeeded = (changes: CartChanges) => {
  if (
    changes.paymentMethod.start?.code === PaymentCodes.ON_DELIVERY &&
    changes.paymentMethod.current?.code === PaymentCodes.BLIK
  ) {
    return true
  }

  if (
    changes.paymentMethod.current?.code === PaymentCodes.BLIK &&
    !isNullOrUndefined(changes.payment.current.diff) &&
    changes.payment.current.diff > 0
  ) {
    return true
  }

  return false
}

interface RestorePreEditCartDeps {
  setCart: (cart: Cart) => void
  createCart: () => Promise<unknown>
}

export const restorePreEditCart = async ({
  setCart,
  createCart
}: RestorePreEditCartDeps): Promise<void> => {
  const oldCart = new LocalStorage(
    EditSaleOrderCartKeys.EDIT_MODE_OLD_CART
  ).get<Cart | null>(null)

  if (oldCart) {
    setCart(oldCart)
  } else {
    await createCart()
  }

  new LocalStorage(EditSaleOrderCartKeys.EDIT_MODE_OLD_CART).remove()
  new LocalStorage(EditSaleOrderCartKeys.EDIT_MODE_START_CART).remove()
  new LocalStorage(EditSaleOrderCartKeys.EDIT_MODE_START_ORDER).remove()
}

export const showPaymentDifference = (
  order: Pick<SaleOrder, 'editPaymentDifference' | 'edited'>
): boolean => {
  const paymentMethod = new LocalStorage(
    EditSaleOrderCartKeys.EDIT_MODE_START_PAYMENT_METHOD
  ).get<PaymentMethod | null>(null)

  const isPaymentOnDelivery = paymentMethod?.code === PaymentCodes.ON_DELIVERY

  return order.editPaymentDifference && order.edited && !isPaymentOnDelivery
}

export const isEditSaleOrderEnabled = (order: SaleOrder) => {
  return Boolean(
    IS_EDIT_MODE_ENABLED &&
      order.isEditable &&
      order.editableUntil &&
      order.editableUntil > new Date().toISOString() &&
      !order.edited
  )
}
